"use client";
import Image from "next/image";
import { SlideUp } from "./slide-up";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { UserIcon, UsersIcon } from "lucide-react";
import { LucideIcon } from "lucide-react";
import loginImage from "../public/login-bg.jpeg";
import Logo from "./Logo";

type AccountType = "individual" | "organization";
interface AccountTypeInfo {
  type: AccountType;
  title: string;
  description: string;
  icon: LucideIcon;
}
interface AccountTypeCardProps {
  isSelected: boolean;
  type: AccountType;
  title: string;
  description: string;
  icon: LucideIcon;
  onSelect: (type: AccountType) => void;
}
interface OnboardingStepsProps {
  accountType: AccountType;
}
interface ContentProps {
  headline: string;
  description: string;
}

const AccountTypeCard = ({
  isSelected,
  type,
  title,
  description,
  icon: Icon,
  onSelect,
}: AccountTypeCardProps) => (
  <div
    className={`rounded-xl p-4 h-full cursor-pointer transition-all duration-300 hover:scale-[1.02] ${
      isSelected ? "bg-white text-gray-900" : "bg-gray-900/25 text-white"
    }`}
    onClick={() => onSelect(type)}
  >
    <div className="flex items-center gap-4">
      <div className="p-3 rounded-lg bg-gray-100">
        <Icon className="h-6 w-6 text-gray-700" />
      </div>
      <div className="tracking-tight">
        <h2 className="text-xl font-medium">{title}</h2>
        <p className="mt-1">{description}</p>
      </div>
    </div>
  </div>
);

const OnboardingSteps = ({ accountType }: OnboardingStepsProps) => {
  const router = useRouter();

  const handleTypeChange = (value: AccountType): void => {
    router.push(`/register?type=${value}`);
  };

  const accountTypes: AccountTypeInfo[] = [
    {
      type: "individual",
      title: "Personal account",
      description: "I want to improve my own health through sound therapy.",
      icon: UserIcon,
    },
    {
      type: "organization",
      title: "Organization account",
      description:
        "I'm setting up sound therapy resources for patients or clients.",
      icon: UsersIcon,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 max-w-xl mx-4">
      {accountTypes.map((account) => (
        <AccountTypeCard
          key={account.type}
          isSelected={accountType === account.type}
          type={account.type}
          title={account.title}
          description={account.description}
          icon={account.icon}
          onSelect={handleTypeChange}
        />
      ))}
    </div>
  );
};

const LoginContent = () => {
  const content: ContentProps = {
    headline: "Music That\nHeals",
    description:
      "Sign in to access your personalized health rhythms and melodies.",
  };

  return (
    <div className="absolute bottom-0 left-8">
      <SlideUp>
        <div className="mb-12 space-y-4 font-serif">
          <h1 className="text-7xl font-bold tracking-wide text-white whitespace-pre-line">
            {content.headline}
          </h1>
          <p className="text-2xl tracking-wide text-white/80">
            {content.description}
          </p>
        </div>
      </SlideUp>
    </div>
  );
};

const RegisterContent = () => {
  const searchParams = useSearchParams();
  const accountType = (searchParams.get("type") as AccountType) || "individual";
  const hasInvitation = !!searchParams.get('invitation');

  const content: ContentProps = {
    headline: "Get Started with HealthTunes",
    description:
      "Set up your account to experience the healing power of therapeutic audio.",
  };

  return (
    <div className="absolute bottom-24 left-0 right-0 flex justify-center">
      <SlideUp>
        <div className="mb-6 max-w-xl text-center font-serif">
          <h1 className="text-5xl font-bold tracking-wide leading-tight text-white whitespace-pre-line">
            {content.headline}
          </h1>
          <p className="text-2xl tracking-wide text-white/80">
            {content.description}
          </p>
        </div>

        {!hasInvitation && <OnboardingSteps accountType={accountType} />}
      </SlideUp>
    </div>
  );
};

const Background = () => (
  <div className="absolute inset-0 z-0">
    <Image
      src={loginImage}
      layout="fill"
      objectFit="cover"
      objectPosition="right"
      priority
      alt="Background"
    />
    <div className="absolute inset-0 bg-black/30" />
  </div>
);

export default function OnBoarding() {
  const pathname = usePathname();
  const isRegisterPage = pathname === "/register";

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Background />

      <div className="relative h-screen p-8 z-10">
        <div className="flex items-center gap-2">
          <SlideUp>
            <Logo
              className="w-[300px]"
              textFill="white"
              circleFill="white"
              noteFill="black"
            />
          </SlideUp>
        </div>

        {isRegisterPage ? <RegisterContent /> : <LoginContent />}
      </div>
    </div>
  );
}
